import { useFeatures } from '@/contexts/root-store-context'
import { type Observable, switchMap } from 'rxjs'
import type { ExitMethod } from '@/features/shell/exiting/ExitMethod'
import { useObservableValue } from '@/hooks/rx-hooks'
import { useMemo } from 'react'
import { toStream } from '@/utils/mobx-util'
import { startWith } from 'rxjs/operators'

export interface UseExitMethodResult {
  /**
   * The type of exit method.
   */
  exitMethodType: ExitMethod['type']
  /**
   * Label/title to apply to the element, if relevant.
   */
  exitMethodLabel: string
  /**
   * Whether the exit method is visible.
   */
  isExitMethodVisible: boolean
  /**
   * Executes the exit method imperatively.
   * Should only be used as a last resort; for wiring up UI elements, use the
   * props instead.
   */
  execute: () => void
  /**
   * Props to spread on to buttons/links to write up the exit button.
   */
  exitMethodProps: {
    href?: string
    to?: string
    onPress?: () => unknown
  }
}

/**
 * Returns props for the page exit method.
 */
export function usePageExitMethod() {
  const { pageExitMethod$ } = useFeatures(({ shell }) => ({
    pageExitMethod$: shell.pageExitMethod$,
  }))

  return useExitMethodCore(pageExitMethod$)
}

/**
 * Returns props for the root exit method.
 */
export function useRootExitMethod() {
  const { rootExitMethod$ } = useFeatures(({ shell }) => ({
    rootExitMethod$: shell.rootExitMethod$,
  }))

  return useExitMethodCore(rootExitMethod$)
}

function useExitMethodCore(
  exitMethod$: Observable<ExitMethod>,
): UseExitMethodResult {
  // Exit methods are based on MobX observables, and we need to turn them
  // into props which we can spread on buttons and links.
  return useObservableValue(
    useMemo(
      () =>
        exitMethod$.pipe(
          switchMap((method) => {
            const transform = (): UseExitMethodResult => {
              const execute = () => method.execute()
              switch (method.type) {
                case 'NavigateToDashboard': {
                  return {
                    exitMethodType: method.type,
                    exitMethodLabel: method.returnLabel,
                    isExitMethodVisible: method.isVisible,
                    execute,
                    exitMethodProps: {
                      to: method.returnUrl,
                    },
                  }
                }
                case 'ReturnToPartner': {
                  return {
                    exitMethodType: method.type,
                    exitMethodLabel: method.returnLabel,
                    isExitMethodVisible: method.isVisible,
                    execute,
                    exitMethodProps: {
                      href: method.supportsDelegatedExit
                        ? undefined
                        : method.returnUrl,
                      onPress: method.supportsDelegatedExit
                        ? execute
                        : undefined,
                    },
                  }
                }
              }
            }

            return toStream(transform).pipe(startWith(transform()))
          }),
        ),
      [exitMethod$],
    ),
  )
}
